import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getPendingDeletions, adminReinstateAccount } from '../../services/api';

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

function formatCurrency(cents) {
  if (!cents && cents !== 0) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
}

function getDaysColor(days) {
  if (days <= 7) return '#ef4444';
  if (days <= 30) return '#f59e0b';
  return '#10b981';
}

const SORTABLE_COLUMNS = [
  { key: 'username', label: 'Username' },
  { key: 'email', label: 'Email' },
  { key: 'deletion_reason', label: 'Reason' },
  { key: 'deletion_scheduled_at', label: 'Requested' },
  { key: 'days_remaining', label: 'Days Left' },
  { key: 'deletion_permanent_at', label: 'Permanent Date' },
  { key: 'account_age_days', label: 'Acct Age' },
  { key: 'total_payments_cents', label: 'Total Paid' },
  { key: 'payment_count', label: 'Payments' },
  { key: 'total_activities', label: 'Activities' },
  { key: 'last_active', label: 'Last Active' },
];

function PendingDeletions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'days_remaining', dir: 'asc' });
  const [reinstatingId, setReinstatingId] = useState(null);
  const [confirmReinstateId, setConfirmReinstateId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getPendingDeletions();
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch pending deletions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedDeletions = useMemo(() => {
    if (!data?.pending_deletions) return [];
    const sorted = [...data.pending_deletions];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Handle string comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.dir === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Numeric comparison
      if (sortConfig.dir === 'asc') return (aVal || 0) - (bVal || 0);
      return (bVal || 0) - (aVal || 0);
    });
    return sorted;
  }, [data, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleReinstate = async (userId) => {
    setReinstatingId(userId);
    try {
      await adminReinstateAccount(userId);
      setConfirmReinstateId(null);
      await fetchData();
    } catch (err) {
      setError(`Failed to reinstate user: ${err.message}`);
    } finally {
      setReinstatingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
        Loading pending deletions...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          padding: '12px 16px',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '6px',
          color: '#fca5a5',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{error}</span>
          <button
            onClick={fetchData}
            style={{
              padding: '4px 12px',
              backgroundColor: 'transparent',
              border: '1px solid #ef4444',
              borderRadius: '4px',
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const deletions = sortedDeletions;

  return (
    <div style={{ padding: '20px' }}>
      {/* Header with refresh */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: '600', margin: 0 }}>
          Pending Account Deletions
        </h2>
        <button
          onClick={fetchData}
          style={{
            padding: '6px 16px',
            backgroundColor: 'transparent',
            border: '1px solid #333',
            borderRadius: '4px',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <p style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px', textTransform: 'uppercase' }}>
            Total Pending
          </p>
          <p style={{ color: '#fff', fontSize: '28px', fontWeight: '700', margin: 0 }}>
            {summary.total_pending || 0}
          </p>
        </div>
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <p style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px', textTransform: 'uppercase' }}>
            Avg Days Remaining
          </p>
          <p style={{ color: '#fff', fontSize: '28px', fontWeight: '700', margin: 0 }}>
            {summary.avg_days_remaining != null ? Math.round(summary.avg_days_remaining) : 0}
          </p>
        </div>
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <p style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px', textTransform: 'uppercase' }}>
            Revenue at Risk
          </p>
          <p style={{ color: '#fff', fontSize: '28px', fontWeight: '700', margin: 0 }}>
            {formatCurrency(summary.total_revenue_cents || 0)}
          </p>
        </div>
      </div>

      {/* Empty state */}
      {deletions.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          color: '#6b7280',
        }}>
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>No pending account deletions</p>
          <p style={{ fontSize: '13px' }}>When users request account deletion, they will appear here.</p>
        </div>
      ) : (
        /* Table */
        <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #333' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
          }}>
            <thead>
              <tr style={{ backgroundColor: '#1a1a1a' }}>
                {SORTABLE_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      color: sortConfig.key === col.key ? '#218aff' : '#9ca3af',
                      fontWeight: '600',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #333',
                      userSelect: 'none',
                    }}
                  >
                    {col.label}
                    {sortConfig.key === col.key && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.dir === 'asc' ? '\u25B2' : '\u25BC'}
                      </span>
                    )}
                  </th>
                ))}
                <th style={{
                  padding: '10px 12px',
                  textAlign: 'left',
                  color: '#9ca3af',
                  fontWeight: '600',
                  borderBottom: '1px solid #333',
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {deletions.map((item) => (
                <tr
                  key={item.user_id}
                  style={{
                    borderBottom: '1px solid #222',
                    backgroundColor: confirmReinstateId === item.user_id ? 'rgba(16, 185, 129, 0.05)' : 'transparent',
                  }}
                >
                  <td style={{ padding: '10px 12px', color: '#fff', fontWeight: '500' }}>
                    {item.username}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <a
                      href={`mailto:${item.email}`}
                      style={{ color: '#218aff', textDecoration: 'none' }}
                    >
                      {item.email}
                    </a>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#d1d5db', maxWidth: '150px' }}>
                    <span title={item.deletion_reason || ''} style={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {item.deletion_reason || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {formatDate(item.deletion_scheduled_at)}
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      color: getDaysColor(item.days_remaining),
                      fontWeight: '600',
                    }}>
                      {item.days_remaining}d
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {formatDate(item.deletion_permanent_at)}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#d1d5db', whiteSpace: 'nowrap' }}>
                    {item.account_age_days}d
                  </td>
                  <td style={{ padding: '10px 12px', color: '#d1d5db', whiteSpace: 'nowrap' }}>
                    {formatCurrency(item.total_payments_cents)}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#d1d5db', textAlign: 'center' }}>
                    {item.payment_count || 0}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#d1d5db', textAlign: 'center' }}>
                    {item.total_activities || 0}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {formatTimeAgo(item.last_active)}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {confirmReinstateId === item.user_id ? (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => handleReinstate(item.user_id)}
                          disabled={reinstatingId === item.user_id}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#10b981',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: reinstatingId === item.user_id ? 'not-allowed' : 'pointer',
                            opacity: reinstatingId === item.user_id ? 0.6 : 1,
                          }}
                        >
                          {reinstatingId === item.user_id ? '...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmReinstateId(null)}
                          disabled={reinstatingId === item.user_id}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: 'transparent',
                            color: '#9ca3af',
                            border: '1px solid #333',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmReinstateId(item.user_id)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: 'transparent',
                          color: '#10b981',
                          border: '1px solid #10b981',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Reinstate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PendingDeletions;
